/**
 * The mutable position used by movegen and, later, by the bot's search.
 *
 * The 2023 build represented a position as an array of `{ playerIndex, type }` objects
 * and produced every successor with `cells.map(...)` — a full board copy, plus one
 * object per piece, for every node visited. That is fine for a UI and hopeless for a
 * search. Here a position is a typed array mutated in place, with `makeMove` returning
 * a packed undo token that `unmakeMove` reverses exactly.
 *
 * `toPosition` / `fromPosition` convert to and from the plain readonly `Position` shape
 * that the wire and the UI use, so nothing outside the engine needs to know about any
 * of this.
 */

import { type Board, getBoard } from './board.ts';
import { makeCells } from './formations.ts';
import type { Piece, PlayerIndex, Position, Variant } from './types.ts';

// --- piece encoding -------------------------------------------------------
//
//   bit 0  occupied
//   bit 1  owner (0 or 1)
//   bit 2  is a pawn (clear means king)
//
// so: K0 = 1, K1 = 3, P0 = 5, P1 = 7, and empty is 0.

export const EMPTY = 0;
export const K0 = 0b001;
export const K1 = 0b011;
export const P0 = 0b101;
export const P1 = 0b111;

export const isOccupied = (c: number): boolean => c !== EMPTY;
export const ownerOf = (c: number): PlayerIndex => ((c >> 1) & 1) as PlayerIndex;
export const isPawn = (c: number): boolean => (c & 0b100) !== 0;
export const isKing = (c: number): boolean => c !== EMPTY && (c & 0b100) === 0;

export const encodePiece = (piece: Piece): number =>
  0b001 | (piece.player << 1) | (piece.type === 'pawn' ? 0b100 : 0);

export const decodePiece = (c: number): Piece | null =>
  c === EMPTY ? null : { player: ownerOf(c), type: isPawn(c) ? 'pawn' : 'king' };

// --- move encoding --------------------------------------------------------
//
//   bits 0-1   type
//   bits 2-9   from cell index
//   bits 10-17 to cell index
//
// Boards top out at 127 cells, so eight bits per endpoint is ample.

export const MOVE_PASS = 0;
export const MOVE_STEP = 1; // an ordinary step *or* a bow glide — see RULES.md §4.1
export const MOVE_SPAWN = 2;
export const MOVE_RESIGN = 3;

export const packMove = (type: number, from = 0, to = 0): number =>
  type | (from << 2) | (to << 10);

export const moveType = (m: number): number => m & 0b11;
export const moveFrom = (m: number): number => (m >> 2) & 0xff;
export const moveTo = (m: number): number => (m >> 10) & 0xff;

/** No formation defines more than three kings per player (triarchy, colonies). */
export const MAX_KINGS = 3;

export interface FastPosition {
  readonly variant: Variant;
  readonly board: Board;
  /** One encoded piece per cell. */
  readonly cells: Int8Array;
  /** Remaining spawns, indexed by player. */
  readonly spawns: Int32Array;
  playerToMove: PlayerIndex;
  /**
   * Cell index of each living king, `MAX_KINGS` slots per player.
   *
   * Maintained incrementally because RULES.md §6.1 needs the king count on every
   * candidate move and the single king's cell whenever that count is one. Rescanning
   * the board for it would put an O(cells) loop inside the legality filter.
   */
  readonly kingCells: Int32Array;
  readonly kingCount: Int32Array;
}

function indexKings(pos: FastPosition): void {
  pos.kingCount[0] = 0;
  pos.kingCount[1] = 0;
  for (let i = 0; i < pos.cells.length; i++) {
    const c = pos.cells[i];
    if (isKing(c)) {
      const p = ownerOf(c);
      if (pos.kingCount[p] >= MAX_KINGS) {
        throw new Error(`Player ${p} has more than ${MAX_KINGS} kings`);
      }
      pos.kingCells[p * MAX_KINGS + pos.kingCount[p]] = i;
      pos.kingCount[p]++;
    }
  }
}

export function createPosition(
  variant: Variant,
  cells: readonly (Piece | null)[],
  playerToMove: PlayerIndex,
  spawns: readonly [number, number],
): FastPosition {
  const board = getBoard(variant.radius);
  if (cells.length !== board.size) {
    throw new Error(
      `Expected ${board.size} cells for radius ${variant.radius}, got ${cells.length}`,
    );
  }

  const pos: FastPosition = {
    variant,
    board,
    cells: Int8Array.from(cells, (c) => (c === null ? EMPTY : encodePiece(c))),
    spawns: Int32Array.from(spawns),
    playerToMove,
    kingCells: new Int32Array(2 * MAX_KINGS).fill(-1),
    kingCount: new Int32Array(2),
  };
  indexKings(pos);
  return pos;
}

/** The starting position for a variant. */
export const initialPosition = (variant: Variant): FastPosition =>
  createPosition(variant, makeCells(variant), 0, [
    variant.spawnsAvailable,
    variant.spawnsAvailable,
  ]);

export const fromPosition = (position: Position): FastPosition =>
  createPosition(position.variant, position.cells, position.playerToMove, [
    position.spawnsAvailable[0],
    position.spawnsAvailable[1],
  ]);

export const toPosition = (pos: FastPosition): Position => ({
  variant: pos.variant,
  playerToMove: pos.playerToMove,
  cells: Array.from(pos.cells, decodePiece),
  spawnsAvailable: [pos.spawns[0], pos.spawns[1]],
});

export function clonePosition(pos: FastPosition): FastPosition {
  return {
    variant: pos.variant,
    board: pos.board,
    cells: pos.cells.slice(),
    spawns: pos.spawns.slice(),
    playerToMove: pos.playerToMove,
    kingCells: pos.kingCells.slice(),
    kingCount: pos.kingCount.slice(),
  };
}

// --- make / unmake --------------------------------------------------------
//
// The undo token packs everything `unmakeMove` needs:
//
//   bits 0-2  the captured piece, or 0
//   bits 3-4  which king slot the captured king occupied, plus one (0 means none)

const undoToken = (captured: number, kingSlot: number): number =>
  captured | ((kingSlot + 1) << 3);

const undoCaptured = (u: number): number => u & 0b111;
const undoKingSlot = (u: number): number => ((u >> 3) & 0b11) - 1;

/** Find which of a player's king slots holds `cell`. */
function kingSlotAt(pos: FastPosition, player: PlayerIndex, cell: number): number {
  const base = player * MAX_KINGS;
  for (let i = 0; i < pos.kingCount[player]; i++) {
    if (pos.kingCells[base + i] === cell) return i;
  }
  throw new Error(`No king of player ${player} at cell ${cell}`);
}

/**
 * Apply a packed move in place, returning a token for `unmakeMove`.
 *
 * The move must be pseudo-legal; this does not validate.
 */
export function makeMove(pos: FastPosition, move: number): number {
  const type = moveType(move);
  const me = pos.playerToMove;
  let captured = EMPTY;
  let capturedKingSlot = -1;

  if (type === MOVE_STEP) {
    const from = moveFrom(move);
    const to = moveTo(move);
    const moved = pos.cells[from];
    captured = pos.cells[to];

    if (captured !== EMPTY) {
      const victim = ownerOf(captured);
      if (isKing(captured)) {
        // Swap-remove from the victim's king list. Order is irrelevant, so the slot
        // index is all `unmakeMove` needs to put things back exactly.
        capturedKingSlot = kingSlotAt(pos, victim, to);
        const base = victim * MAX_KINGS;
        const last = pos.kingCount[victim] - 1;
        pos.kingCells[base + capturedKingSlot] = pos.kingCells[base + last];
        pos.kingCells[base + last] = -1;
        pos.kingCount[victim] = last;
      }
      // Recruitment: a king that captures anything gains a spawn (RULES.md §3).
      if (isKing(moved)) pos.spawns[me]++;
    }

    pos.cells[to] = moved;
    pos.cells[from] = EMPTY;

    if (isKing(moved)) {
      pos.kingCells[me * MAX_KINGS + kingSlotAt(pos, me, from)] = to;
    }
  } else if (type === MOVE_SPAWN) {
    pos.cells[moveTo(move)] = me === 0 ? P0 : P1;
    pos.spawns[me]--;
  }
  // MOVE_PASS and MOVE_RESIGN change nothing but the turn.

  pos.playerToMove = (me ^ 1) as PlayerIndex;
  return undoToken(captured, capturedKingSlot);
}

/** Reverse `makeMove`. `move` and `undo` must be the exact pair that was applied. */
export function unmakeMove(pos: FastPosition, move: number, undo: number): void {
  const type = moveType(move);
  const me = (pos.playerToMove ^ 1) as PlayerIndex;
  pos.playerToMove = me;

  if (type === MOVE_STEP) {
    const from = moveFrom(move);
    const to = moveTo(move);
    const moved = pos.cells[to];
    const captured = undoCaptured(undo);

    pos.cells[from] = moved;
    pos.cells[to] = captured;

    if (isKing(moved)) {
      pos.kingCells[me * MAX_KINGS + kingSlotAt(pos, me, to)] = from;
    }

    if (captured !== EMPTY) {
      if (isKing(moved)) pos.spawns[me]--;

      if (isKing(captured)) {
        const victim = ownerOf(captured);
        const base = victim * MAX_KINGS;
        const slot = undoKingSlot(undo);
        const count = pos.kingCount[victim];
        // Undo the swap-remove: the element that was moved into `slot` goes back to
        // the end, and the captured king returns to `slot`.
        pos.kingCells[base + count] = pos.kingCells[base + slot];
        pos.kingCells[base + slot] = to;
        pos.kingCount[victim] = count + 1;
      }
    }
  } else if (type === MOVE_SPAWN) {
    pos.cells[moveTo(move)] = EMPTY;
    pos.spawns[me]++;
  }
}
